import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  useDeletePrincipalEntriesMutation,
  useFetchPrincipalEntitiesdataQuery,
  useUpdatePrincipalEntriesMutation,
} from "../../store/services/principleApi";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CommonModal from "../common/commonModal";
import { maskNumber, maskNumberAdvanced } from "../common/Encryption";
import { cellSX } from "../common/commonStyle";

const PrincipalEntityList = () => {
  const dispatch = useDispatch();
  const { data: entriesData, isLoading } = useFetchPrincipalEntitiesdataQuery();
  const [deletePrincipal] = useDeletePrincipalEntriesMutation();
  const [updatePrincipal] = useUpdatePrincipalEntriesMutation();

  const [editEntity, setEditEntity] = useState(null);
  const [deleteEntity, setDeleteEntity] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [peName, setPeName] = useState("");
  const [peId, setPeId] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleEdit = (entity) => {
    setEditEntity(entity);
    setPeName(entity.pe_name);
    setPeId(entity.pe_id);
    setIsEditing(true);
    setOpenModal(true);
  };

  const handleDelete = (entity) => {
    setDeleteEntity(entity);
    setIsEditing(false);
    setOpenModal(true);
  };

  const handleConfirmDelete = async (entityId) => {
    const response = await deletePrincipal(entityId);
    console.log({ response });

    if (response?.data) {
      setOpenModal(false);
      setDeleteEntity(null);
    } else {
      console.error("Failed to delete entity:", response);
    }
  };

  const handleEditSubmit = async () => {
    setLoading(true);

    const data = {
      pe_id: peId,
      pe_name: peName,
      is_active: true,
      principalId: editEntity?.principalentity_id,
    };

    try {
      const res = await updatePrincipal(data);

      if (res?.data?.data) {
        setOpenModal(false);
        setEditEntity(null);
        alert(res?.data?.message || "Entity updated successfully");
      } else {
        console.error("Failed to update entity:", res);
        // fallback (rare case)
        alert(res?.error?.data?.message || "Failed to update");
      }

    } catch (error) {
      console.error(error);

      // 👇 show backend validation error
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred while updating";

      alert(errorMessage);

    } finally {
      setLoading(false);
    }
  };
  const tableCellBorder = {
    borderRight: "0.5px solid #1b1b1bff",
  };

  return (
    <div className="mt-4">
      {/* <h3 className="text-lg font-semibold">List of Principal Entities</h3> */}
      {entriesData?.data?.length === 0 ? (
        <div>No principal entities available</div>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 0.5, border: "1px solid #1b1b1bff", mt: 2 }}
        >
          <Table>
            <TableHead
              sx={{
                bgcolor: "#0288d1",
                "& th": {
                  fontWeight: "bold",
                  color: "white",
                  fontSize: "0.75rem",
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ ...tableCellBorder }}>Serial.No</TableCell>
                <TableCell sx={{ ...tableCellBorder }}>Entity ID</TableCell>
                <TableCell sx={{ ...tableCellBorder }}>Name</TableCell>
                <TableCell sx={{ ...tableCellBorder }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entriesData?.data?.map((entity, index) => (
                <TableRow
                  key={entity.principalentity_id}
                  sx={{
                    "& td": {
                      ...tableCellBorder,
                      padding: "8px",
                    },
                  }}
                >
                  <TableCell> {index + 1}</TableCell>
                  <TableCell sx={cellSX}>{maskNumber(entity.pe_id)}</TableCell>
                  <TableCell sx={cellSX}>{entity.pe_name}</TableCell>
                  <TableCell sx={cellSX}>
                    <EditIcon
                      onClick={() => handleEdit(entity)}
                      variant="contained"
                      size="small"
                      sx={{ marginRight: 2, cursor: "pointer" }}
                    />
                    <DeleteIcon
                      onClick={() => handleDelete(entity)}
                      variant="contained"
                      size="small"
                      sx={{
                        cursor: entity.found ? "not-allowed" : "pointer",
                        opacity: entity.found ? 0.5 : 1,
                        pointerEvents: "auto",
                        marginLeft: 1,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CommonModal open={openModal} setOpen={setOpenModal}>
        {isEditing ? (
          <Box sx={{ p: 2 }}>
            {errors.peName && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mb: 1, display: "block", fontSize: "0.7rem" }}
              >
                {errors.peName}
              </Typography>
            )}
            <TextField
              label="Entity Name"
              variant="outlined"
              fullWidth
              value={peName}
              onChange={(e) => setPeName(e.target.value)}
              size="small"
              sx={{
                mb: 2,
                "& .MuiInputBase-input": { fontSize: "0.75rem" },
                "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
              }}
            />

            {errors.peId && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mb: 1, display: "block", fontSize: "0.7rem" }}
              >
                {errors.peId}
              </Typography>
            )}
            <TextField
              label="Entity ID"
              variant="outlined"
              fullWidth
              // value={peId}
              value={maskNumber(peId)}
              onChange={(e) => setPeId(e.target.value)}
              size="small"
              sx={{
                mb: 3,
                "& .MuiInputBase-input": { fontSize: "0.75rem" },
                "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
              }}
            />

            <Box
              sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
            >
              <Button
                onClick={handleEditSubmit}
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  bgcolor: loading ? "grey.400" : "#1976d2",
                }}
              >
                Save Changes
              </Button>
              <Button
                onClick={() => setOpenModal(false)}
                variant="outlined"
                color="error"
                fullWidth
                sx={{ fontSize: "0.7rem", textTransform: "none" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography sx={{ mb: 2, fontSize: "0.9rem" }}>
              {deleteEntity?.found
                ? "This Principal Entity is associated with a customer and cannot be deleted."
                : "Are you sure you want to delete this Principal Entity?"}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              {!deleteEntity?.found && (
                <Button
                  onClick={() =>
                    handleConfirmDelete(deleteEntity?.principalentity_id)
                  }
                  color="error"
                  variant="contained"
                  sx={{ fontSize: "0.75rem", width: "30%" }}
                >
                  Yes
                </Button>
              )}
              <Button
                onClick={() => setOpenModal(false)}
                sx={{
                  fontSize: "0.75rem",
                  width: deleteEntity?.found ? "100%" : "30%",
                }}
                variant="outlined"
                color="error"
              >
                {deleteEntity?.found ? "Close" : "Cancel"}
              </Button>
            </Box>
          </Box>
        )}
      </CommonModal>
    </div>
  );
};

export default PrincipalEntityList;
